/**
 * Formatters for campaign meta information displayed under the campaign title.
 * (Publication period, budget, influencer count)
 */

/**
 * Format a campaign's publication date range as a human-readable string.
 * Handles single date, range, and empty input.
 */
export function formatCampaignDateRange(publicationDates?: string[]): string {
  if (!publicationDates?.length) return '';

  // Epoch-zero dates (1970-01-01) are sentinel "no date" values — exclude them.
  const MIN_VALID_YEAR_TS = new Date('2000-01-01').getTime();
  const timestamps = publicationDates
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t) && t >= MIN_VALID_YEAR_TS);

  if (timestamps.length === 0) return '';

  const minTs = Math.min(...timestamps);
  const maxTs = Math.max(...timestamps);
  const first = new Date(minTs);
  const last = new Date(maxTs);

  // publicationDates are date-only "YYYY-MM-DD" values stored as UTC midnight. Format them in UTC so
  // the calendar day is preserved — a browser-local toLocaleDateString (no timeZone) shifts them a day
  // for viewers behind UTC (e.g. PT: "2025-06-10T00:00:00Z" would render "Jun 9").
  const fmtFull = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const fmtShort = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

  if (minTs === maxTs) return fmtFull(first);

  // Same year: "Jan 1 – Mar 31, 2025" (compare in UTC to match the UTC-formatted output above)
  if (first.getUTCFullYear() === last.getUTCFullYear()) {
    return `${fmtShort(first)} – ${fmtFull(last)}`;
  }
  return `${fmtFull(first)} – ${fmtFull(last)}`;
}

/**
 * Format a campaign budget with currency symbol.
 * Defaults to USD ($).
 */
export function formatCampaignBudget(budget?: number, currency?: string): string {
  if (budget == null || !Number.isFinite(budget)) return '';
  const symbol = currency === 'KRW' ? '₩' : '$';
  return `${symbol}${budget.toLocaleString()}`;
}
